import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  Search, Building2, Crown, Calendar, Users, Ban, DollarSign, Activity,
  TrendingUp, Shield, Mail, User as UserIcon, BarChart3, PieChart,
  Plus, Pencil, Trash2, X, Eye, EyeOff, RefreshCw, ChevronDown, AlertTriangle, Phone
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { formatDate } from '@/utils/helpers';
import api from '@/utils/api';
import StatCard from '@/components/ui/StatCard';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ErrorMessage from '@/components/ui/ErrorMessage';
import EmptyState from '@/components/ui/EmptyState';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

/* ─── helpers ────────────────────────────────────────────── */
const ROLES = ['admin', 'gestor', 'colaborador', 'super_admin'];
const PLANS = ['basico', 'intermediario', 'full'];
const PLAN_LABELS = { basico: 'Básico', intermediario: 'Intermediário', full: 'Full' };
const PLAN_COLORS = {
  basico: 'bg-slate-100 text-slate-700 border-slate-200',
  intermediario: 'bg-blue-50 text-blue-700 border-blue-200',
  full: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};
const currencyFormat = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

/* ─── Modal base ─────────────────────────────────────────── */
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

/* ─── Modal: Criar/Editar Usuário ────────────────────────── */
function UserModal({ user, companies, onClose, onSaved }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    role: user?.role || 'colaborador',
    company_id: user?.company_id || (companies[0]?.id || ''),
  });
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company_id) {
      toast.error('Preencha nome, e-mail e empresa.');
      return;
    }
    if (!isEdit && !form.password) {
      toast.error('Informe uma senha para o novo usuário.');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { name: form.name, email: form.email, role: form.role, company_id: form.company_id };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/system/users/${user.id}`, payload);
        toast.success('Usuário atualizado!');
      } else {
        await api.post('/admin/system/users', form);
        toast.success('Usuário criado com sucesso!');
      }
      onSaved();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Erro ao salvar usuário.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isEdit ? `Editar: ${user.name}` : 'Criar Novo Usuário'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="text-xs font-medium text-slate-700 mb-1.5 block">Nome completo *</label>
          <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="João Silva" required />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-700 mb-1.5 block">E-mail *</label>
          <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="joao@empresa.com" required />
        </div>
        <div className="relative">
          <label className="text-xs font-medium text-slate-700 mb-1.5 block">
            {isEdit ? 'Nova senha (deixe em branco para não alterar)' : 'Senha *'}
          </label>
          <Input
            type={showPass ? 'text' : 'password'}
            value={form.password}
            onChange={e => set('password', e.target.value)}
            placeholder={isEdit ? '••••••••' : 'Mínimo 6 caracteres'}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPass(s => !s)}
            className="absolute right-3 top-[34px] text-slate-400 hover:text-slate-600"
          >
            {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-700 mb-1.5 block">Cargo / Role *</label>
          <select
            value={form.role}
            onChange={e => set('role', e.target.value)}
            className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-700 mb-1.5 block">Empresa *</label>
          <select
            value={form.company_id}
            onChange={e => set('company_id', e.target.value)}
            className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Selecione uma empresa...</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-10 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 h-10 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-60"
          >
            {saving ? 'Salvando...' : isEdit ? 'Salvar alterações' : 'Criar usuário'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ─── Modal: Trocar Plano de Empresa ─────────────────────── */
function ChangePlanModal({ company, onClose, onSaved }) {
  const [plan, setPlan] = useState(company.plan || 'basico');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/system/companies/${company.id}/plan`, { plan });
      toast.success(`Plano da ${company.name} alterado para ${PLAN_LABELS[plan]}!`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao trocar plano.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Plano: ${company.name}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <p className="text-sm text-slate-500">
          Plano atual: <span className="font-semibold text-slate-800">{PLAN_LABELS[company.plan] || company.plan}</span>
        </p>
        <div className="space-y-3">
          {PLANS.map(p => (
            <label
              key={p}
              className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${plan === p ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'}`}
            >
              <input type="radio" name="plan" value={p} checked={plan === p} onChange={() => setPlan(p)} className="hidden" />
              <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${plan === p ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300'}`}>
                {plan === p && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">{PLAN_LABELS[p]}</p>
                <p className="text-xs text-slate-500">
                  {p === 'basico' ? 'R$ 69,90/mês — até 3 usuários' : p === 'intermediario' ? 'R$ 89,90/mês — até 10 usuários' : 'R$ 99,90/mês — usuários ilimitados'}
                </p>
              </div>
            </label>
          ))}
        </div>
        {plan !== company.plan && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">A mudança de plano é imediata e não gera cobrança automática.</p>
          </div>
        )}
        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving || plan === company.plan}
            className="flex-1 h-10 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Salvando...' : 'Confirmar troca'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ─── Modal: Confirmar exclusão ─────────────────────────── */
function DeleteUserModal({ user, onClose, onConfirmed }) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/system/users/${user.id}`);
      toast.success(`Usuário ${user.name} removido.`);
      onConfirmed();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao remover usuário.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal title="Confirmar exclusão" onClose={onClose}>
      <div className="space-y-4">
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">
            Você está prestes a remover <strong>{user.name}</strong> ({user.email}). Esta ação é reversível somente via banco de dados.
          </p>
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
            Cancelar
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex-1 h-10 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-60"
          >
            {deleting ? 'Removendo...' : 'Sim, remover'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ─── Modal: Confirmar exclusão Empresa ──────────────────── */
function DeleteCompanyModal({ company, onClose, onConfirmed }) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/system/companies/${company.id}`);
      toast.success(`Empresa ${company.name} removida com sucesso!`);
      onConfirmed();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao remover empresa.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal title="Confirmar exclusão de Empresa" onClose={onClose}>
      <div className="space-y-4">
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">
            Atenção: Você está prestes a excluir a empresa <strong>{company.name}</strong> e todos os usuários vinculados a ela. Esta ação é irreversível.
          </p>
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
            Cancelar
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex-1 h-10 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-60"
          >
            {deleting ? 'Deletando...' : 'Excluir Definitivamente'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ─── Modal: Detalhes da Empresa ────────────────────────── */
function CompanyDetailModal({ company, onClose }) {
  const isTrial = company.trial_ends_at && new Date(company.trial_ends_at) > new Date();
  const isExpired = company.trial_ends_at && new Date(company.trial_ends_at) <= new Date() && company.plan === 'basico'; // Ajustar lógica conforme necessidade

  const getStatusBadge = () => {
    if (isTrial) return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Trial Ativo</Badge>;
    if (new Date(company.trial_ends_at) <= new Date()) return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Expirado/Inativo</Badge>;
    return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Assinatura Ativa</Badge>;
  };

  const whatsappUrl = company.owner_phone 
    ? `https://api.whatsapp.com/send?phone=55${company.owner_phone.replace(/\D/g, '')}`
    : null;

  return (
    <Modal title="Detalhes do Cliente" onClose={onClose}>
      <div className="space-y-6">
        {/* Header com Avatar */}
        <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
          <div className="h-16 w-16 rounded-2xl bg-white border border-slate-200 flex items-center justify-center overflow-hidden shadow-sm">
            {company.owner_avatar_url ? (
              <img src={`${api.defaults.baseURL}${company.owner_avatar_url}`} alt="" className="h-full w-full object-cover" />
            ) : (
              <Building2 className="h-8 w-8 text-slate-300" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900 leading-tight">{company.name}</h3>
            <div className="mt-1 flex items-center gap-2">
              {getStatusBadge()}
              <span className="text-xs text-slate-400">ID: {company.id?.slice(0, 8)}</span>
            </div>
          </div>
        </div>

        {/* Informações de Contato */}
        <div className="grid grid-cols-1 gap-4">
          <div className="p-4 rounded-xl border border-slate-100 space-y-3">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Contato do Proprietário</h4>
            <div className="space-y-2.5">
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <Mail className="h-4 w-4 text-slate-400" />
                <span>{company.owner_email || company.email}</span>
              </div>
              {company.owner_phone && (
                <a 
                  href={whatsappUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-sm text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  <span>{company.owner_phone} (WhatsApp)</span>
                </a>
              )}
              {!company.owner_phone && (
                <div className="flex items-center gap-3 text-sm text-slate-400 italic">
                  <Phone className="h-4 w-4" />
                  <span>Telefone não informado</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Métricas da Agência */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 rounded-xl border border-slate-100 bg-emerald-50/30">
            <p className="text-xs font-medium text-emerald-600 mb-1">Faturamento Médio</p>
            <p className="text-lg font-bold text-slate-900">{currencyFormat(company.average_revenue)}</p>
          </div>
          <div className="p-4 rounded-xl border border-slate-100 bg-blue-50/30">
            <p className="text-xs font-medium text-blue-600 mb-1">Funcionários</p>
            <p className="text-lg font-bold text-slate-900">{company.employee_count || 0} pessoas</p>
          </div>
        </div>

        {/* Dados de Assinatura */}
        <div className="p-4 rounded-xl border border-slate-100 space-y-3">
          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Assinatura</h4>
          <div className="grid grid-cols-2 gap-y-3 gap-x-6">
            <div>
              <p className="text-[10px] text-slate-400 uppercase font-semibold">Plano Atual</p>
              <p className="text-sm font-semibold text-slate-900">{PLAN_LABELS[company.plan] || company.plan}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase font-semibold">Validade</p>
              <p className="text-sm font-semibold text-slate-900">{formatDate(company.trial_ends_at)}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase font-semibold">Criado em</p>
              <p className="text-sm font-semibold text-slate-700">{formatDate(company.created_at)}</p>
            </div>
            {company.asaas_customer_id && (
              <div>
                <p className="text-[10px] text-slate-400 uppercase font-semibold">ASAAS ID</p>
                <code className="text-xs text-indigo-600">{company.asaas_customer_id}</code>
              </div>
            )}
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full h-11 flex items-center justify-center rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
        >
          Fechar Detalhes
        </button>
      </div>
    </Modal>
  );
}

/* ─── Main Component ─────────────────────────────────────── */
export default function MasterPanel() {
  const { user, refreshAuth } = useAuth();
  const [dataPayload, setDataPayload] = useState(null);
  const [globalUsers, setGlobalUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('users');

  // modals
  const [userModal, setUserModal] = useState(null); // null | 'create' | user-obj
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [planCompany, setPlanCompany] = useState(null);
  const [deleteCompany, setDeleteCompany] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const isAccessAllowed = () =>
    user?.email === 'adrian@kodestudio.com.br' || user?.role === 'super_admin';

  const fetchSystemInfo = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (refreshAuth) {
        await refreshAuth().catch(() => {});
      }
      const [compRes, usersRes] = await Promise.all([
        api.get('/admin/system/companies').catch(() => ({ data: null })),
        api.get('/admin/system/users').catch(() => ({ data: [] })),
      ]);
      setDataPayload(
        compRes.data || {
          companies: [],
          metrics: { mrr: 0, total_empresas: 0, total_usuarios: 0, empresas_trial: 0, empresas_pagas: 0 },
          revenue_chart: [],
          growth_chart: [],
        }
      );
      setGlobalUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch {
      setError('Não foi possível carregar os dados do painel.');
    } finally {
      setLoading(false);
    }
  }, [refreshAuth]);

  useEffect(() => { fetchSystemInfo(); }, [fetchSystemInfo]);

  if (!isAccessAllowed()) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-center h-[50vh]">
        <Ban className="h-16 w-16 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-slate-900">Acesso Restrito</h2>
        <p className="text-slate-500 max-w-md mt-2">Você não possui privilégios de Super Admin.</p>
      </div>
    );
  }

  if (loading) return <LoadingSpinner message="Carregando painel de governança..." />;
  if (error) return <ErrorMessage message={error} onRetry={fetchSystemInfo} />;

  const companiesList = Array.isArray(dataPayload) ? dataPayload : (dataPayload?.companies || []);
  const metrics = dataPayload?.metrics || { mrr: 0, total_empresas: 0, total_usuarios: 0, empresas_trial: 0, empresas_pagas: 0 };
  const revenueChart = dataPayload?.revenue_chart || [];
  const growthChart = dataPayload?.growth_chart || [];

  const filteredCompanies = companiesList.filter(c =>
    (c?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c?.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = globalUsers.filter(u =>
    (u?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u?.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u?.company_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const TABS = [
    { key: 'users', icon: Users, label: `Usuários (${globalUsers.length})` },
    { key: 'tenants', icon: Building2, label: `Empresas (${companiesList.length})` },
  ];

  return (
    <div className="space-y-6">
      {/* Modals */}
      {(userModal === 'create' || editUser) && (
        <UserModal
          user={editUser || null}
          companies={companiesList}
          onClose={() => { setUserModal(null); setEditUser(null); }}
          onSaved={fetchSystemInfo}
        />
      )}
      {deleteUser && (
        <DeleteUserModal
          user={deleteUser}
          onClose={() => setDeleteUser(null)}
          onConfirmed={fetchSystemInfo}
        />
      )}
      {planCompany && (
        <ChangePlanModal
          company={planCompany}
          onClose={() => setPlanCompany(null)}
          onSaved={fetchSystemInfo}
        />
      )}
      {deleteCompany && (
        <DeleteCompanyModal
          company={deleteCompany}
          onClose={() => setDeleteCompany(null)}
          onConfirmed={fetchSystemInfo}
        />
      )}
      {selectedCompany && (
        <CompanyDetailModal
          company={selectedCompany}
          onClose={() => setSelectedCompany(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <div className="h-10 w-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center">
              <Crown className="h-5 w-5 text-white" />
            </div>
            Painel Master
          </h1>
          <p className="text-slate-500 mt-1">Governança global da plataforma Kode Studio</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder={activeTab === 'users' ? 'Buscar usuário...' : 'Buscar empresa...'}
              className="pl-10 h-10"
            />
          </div>
          <button
            onClick={fetchSystemInfo}
            className="h-10 w-10 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors text-slate-500 flex-shrink-0"
            title="Recarregar dados"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          {activeTab === 'users' && (
            <button
              onClick={() => { setEditUser(null); setUserModal('create'); }}
              className="h-10 px-4 flex items-center gap-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-colors flex-shrink-0"
            >
              <Plus className="h-4 w-4" />
              Novo usuário
            </button>
          )}
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard icon={DollarSign} title="MRR" value={currencyFormat(metrics.mrr)} subtitle="Receita Mensal" color="emerald" />
        <StatCard icon={Building2} title="Empresas" value={metrics.total_empresas || 0} subtitle="Tenants Ativos" color="indigo" />
        <StatCard icon={Users} title="Usuários" value={metrics.total_usuarios || 0} subtitle="Contas Globais" color="blue" />
        <StatCard icon={Activity} title="Em Trial" value={metrics.empresas_trial || 0} subtitle="Período Grátis" color="amber" />
        <StatCard icon={TrendingUp} title="Pagantes" value={metrics.empresas_pagas || 0} subtitle="Premium" color="emerald" />
      </div>

      {/* Charts */}
      {revenueChart.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="h-4 w-4 text-emerald-600" />
              <h3 className="text-sm font-semibold text-slate-700">Receita Mensal (Projeção)</h3>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={revenueChart}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip formatter={v => currencyFormat(v)} contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }} />
                <Bar dataKey="receita" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <PieChart className="h-4 w-4 text-indigo-600" />
              <h3 className="text-sm font-semibold text-slate-700">Novas Empresas / Mês</h3>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={growthChart}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }} />
                <Line type="monotone" dataKey="empresas" stroke="#6366f1" strokeWidth={2.5} dot={{ fill: '#6366f1', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center justify-between">
        <div className="flex space-x-1 bg-slate-100 p-1 rounded-lg w-fit">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setSearchTerm(''); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === tab.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-400">
          {activeTab === 'users' ? filteredUsers.length : filteredCompanies.length} resultados
        </p>
      </div>

      {/* ─── TABELA USUÁRIOS ─────────────────────────── */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="h-11 px-5 font-medium text-xs text-slate-500 uppercase tracking-wider">Usuário</th>
                  <th className="h-11 px-5 font-medium text-xs text-slate-500 uppercase tracking-wider">Role</th>
                  <th className="h-11 px-5 font-medium text-xs text-slate-500 uppercase tracking-wider">Empresa</th>
                  <th className="h-11 px-5 font-medium text-xs text-slate-500 uppercase tracking-wider">Plano</th>
                  <th className="h-11 px-5 font-medium text-xs text-slate-500 uppercase tracking-wider">Criado em</th>
                  <th className="h-11 px-5 font-medium text-xs text-slate-500 uppercase tracking-wider text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.length === 0 ? (
                  <tr><td colSpan="6"><EmptyState title="Nenhum usuário encontrado" /></td></tr>
                ) : (
                  filteredUsers.map(u => (
                    <tr key={u.id} className="hover:bg-slate-50/80 transition-colors group">
                      <td className="p-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${u.role?.includes('admin') || u.role === 'super_admin' ? 'bg-indigo-50' : 'bg-slate-100'}`}>
                            {u.role === 'super_admin'
                              ? <Crown className="h-4 w-4 text-yellow-500" />
                              : u.role?.includes('admin')
                                ? <Shield className="h-4 w-4 text-indigo-600" />
                                : <UserIcon className="h-4 w-4 text-slate-500" />}
                          </div>
                          <div>
                            <p className="font-semibold text-sm text-slate-900">{u.name}</p>
                            <p className="text-[11px] text-slate-400 flex items-center gap-1">
                              <Mail className="h-3 w-3" /> {u.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 px-5">
                        <Badge variant="outline" className={
                          u.role === 'super_admin' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                          u.role?.includes('admin') ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                          u.role === 'gestor' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : ''
                        }>
                          {u.role || 'N/A'}
                        </Badge>
                      </td>
                      <td className="p-4 px-5">
                        <div className="flex items-center gap-1.5">
                          <Building2 className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                          <span className="text-sm text-slate-700 truncate max-w-[140px]">{u.company_name || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="p-4 px-5">
                        <Badge
                          variant="outline"
                          className={PLAN_COLORS[u.company_plan] || 'bg-slate-100 text-slate-600'}
                        >
                          {PLAN_LABELS[u.company_plan] || (u.company_plan || 'N/A')}
                        </Badge>
                      </td>
                      <td className="p-4 px-5 text-xs text-slate-500">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-3 w-3" />
                          {formatDate(u.created_at)}
                        </div>
                      </td>
                      <td className="p-4 px-5">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => setEditUser(u)}
                            className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-slate-400 hover:text-indigo-600 transition-colors"
                            title="Editar usuário"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => setDeleteUser(u)}
                            className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                            title="Remover usuário"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── TABELA EMPRESAS ─────────────────────────── */}
      {activeTab === 'tenants' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="h-11 px-5 font-medium text-xs text-slate-500 uppercase tracking-wider">Empresa</th>
                  <th className="h-11 px-5 font-medium text-xs text-slate-500 uppercase tracking-wider">E-mail</th>
                  <th className="h-11 px-5 font-medium text-xs text-slate-500 uppercase tracking-wider">Plano</th>
                  <th className="h-11 px-5 font-medium text-xs text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="h-11 px-5 font-medium text-xs text-slate-500 uppercase tracking-wider">Asaas</th>
                  <th className="h-11 px-5 font-medium text-xs text-slate-500 uppercase tracking-wider">Criação</th>
                  <th className="h-11 px-5 font-medium text-xs text-slate-500 uppercase tracking-wider text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredCompanies.length === 0 ? (
                  <tr><td colSpan="7"><EmptyState title="Nenhuma empresa encontrada" /></td></tr>
                ) : (
                  filteredCompanies.map(company => {
                    const isTrial = company.trial_ends_at && new Date(company.trial_ends_at) > new Date();
                    return (
                      <tr 
                        key={company.id} 
                        className="hover:bg-slate-50/80 transition-colors group cursor-pointer"
                        onClick={() => setSelectedCompany(company)}
                      >
                        <td className="p-4 px-5">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 border border-indigo-100 overflow-hidden shadow-sm">
                              {company.owner_avatar_url ? (
                                <img src={`${api.defaults.baseURL}${company.owner_avatar_url}`} alt="" className="h-full w-full object-cover" />
                              ) : (
                                <Building2 className="h-5 w-5" />
                              )}
                            </div>
                            <div>
                              <p className="font-semibold text-sm text-slate-900 group-hover:text-indigo-600 transition-colors">{company.name}</p>
                              <p className="text-[11px] text-slate-400">ID: {company.id?.slice(0, 8)}...</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 px-5 text-sm text-slate-700 truncate max-w-[180px]">{company.email}</td>
                        <td className="p-4 px-5">
                          <Badge variant="outline" className={PLAN_COLORS[company.plan] || ''}>
                            {PLAN_LABELS[company.plan] || String(company.plan).toUpperCase()}
                          </Badge>
                        </td>
                        <td className="p-4 px-5">
                          {isTrial
                            ? <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50 text-xs">Trial</Badge>
                            : <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50 text-xs">Ativo</Badge>
                          }
                        </td>
                        <td className="p-4 px-5">
                          {company.asaas_customer_id
                            ? <code className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{company.asaas_customer_id}</code>
                            : <span className="text-xs text-amber-600">Pendente</span>}
                        </td>
                        <td className="p-4 px-5 text-xs text-slate-500">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="h-3 w-3" />
                            {formatDate(company.created_at)}
                          </div>
                        </td>
                        <td className="p-4 px-5" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => { e.stopPropagation(); setPlanCompany(company); }}
                              className="h-8 px-3 flex items-center gap-1.5 rounded-lg hover:bg-indigo-50 text-slate-400 hover:text-indigo-600 transition-colors text-xs font-medium"
                              title="Trocar plano"
                            >
                              <ChevronDown className="h-3.5 w-3.5" />
                              Plano
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setDeleteCompany(company); }}
                              className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                              title="Deletar empresa"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
